import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldStar, ArrowRight, ArrowLeft, Certificate, GraduationCap, SignOut,
  EnvelopeSimple, ArrowsClockwise, LockKey, CheckCircle, PlayCircle, Download,
  QrCode, Clock, Sparkle,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLUE = "#0B2B66", GOLD = "#C9A227";
const TOK_KEY = "rescigo_trainee_token";
const USER_KEY = "rescigo_trainee_user";

// ============================== Helpers ==============================
const trainee = {
  get: () => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); }
    catch { return null; }
  },
  token: () => localStorage.getItem(TOK_KEY) || "",
  set: (tok, u) => { localStorage.setItem(TOK_KEY, tok); localStorage.setItem(USER_KEY, JSON.stringify(u)); },
  clear: () => { localStorage.removeItem(TOK_KEY); localStorage.removeItem(USER_KEY); },
};

const tapi = axios.create({ baseURL: API });
tapi.interceptors.request.use((cfg) => {
  const t = trainee.token();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || e?.message || "Something went wrong";
};

// ============================== Razorpay helper ==============================
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

async function launchRazorpayForEnrollment(enr, course, onSuccess) {
  const ok = await loadRazorpayScript();
  if (!ok) { toast.error("Could not load Razorpay. Check your connection."); return; }
  let order;
  try {
    const r = await tapi.post(`/training/me/enroll/${enr.id}/pay-order`);
    order = r.data;
  } catch (e) { toast.error(fmtErr(e)); return; }
  const opts = {
    key: order.key_id, amount: order.amount, currency: order.currency,
    name: "Rescigo Academy", description: course?.name || "Training Enrollment",
    order_id: order.order_id, prefill: order.prefill || {},
    theme: { color: "#0B2B66" },
    handler: async (resp) => {
      try {
        await tapi.post("/training/me/enrollment-verify", {
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
          enrollment_id: enr.id,
        });
        toast.success("Payment successful · Enrollment activated");
        if (onSuccess) await onSuccess();
      } catch (e) { toast.error(fmtErr(e)); }
    },
    modal: { ondismiss: () => toast.message("Payment cancelled — you can retry anytime.") },
  };
  const rz = new window.Razorpay(opts);
  rz.on("payment.failed", (ev) => {
    toast.error(`Payment failed: ${ev?.error?.description || "unknown error"}`);
  });
  rz.open();
}

// Require auth HOC
const RequireTrainee = ({ children }) => {
  const u = trainee.get();
  if (!u || !trainee.token()) return <TrainingLogin />;
  return children;
};

// ============================== LOGIN ==============================
export function TrainingLogin() {
  const nav = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("customer");
  const [challengeId, setChallengeId] = useState("");
  const [otp, setOtp] = useState("");
  const [ttl, setTtl] = useState(0);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (step !== "otp") return;
    const t = setInterval(() => setTtl((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [step]);

  const submitEmail = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/training/auth/login-otp`, { email, user_type: userType });
      setChallengeId(r.data.challenge_id); setTtl(r.data.ttl_seconds || 600); setStep("otp");
    } catch (ex) { setErr(fmtErr(ex)); }
    setBusy(false);
  };

  const submitOtp = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/training/auth/login-verify`, { challenge_id: challengeId, otp });
      trainee.set(r.data.access_token, r.data.trainee);
      nav("/training/home");
    } catch (ex) { setErr(fmtErr(ex)); }
    setBusy(false);
  };

  const mm = String(Math.floor(ttl / 60)).padStart(2, "0");
  const ss = String(ttl % 60).padStart(2, "0");

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
         style={{ background: `linear-gradient(135deg, ${BLUE}, #081C48 60%, #05152E)` }}
         data-testid="training-login">
      {/* subtle gold particles */}
      <div className="absolute inset-0 opacity-20 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #C9A22744, transparent 40%), radial-gradient(circle at 80% 70%, #C9A22733, transparent 50%)" }}/>
      <Link to="/training" className="absolute top-6 left-6 text-xs text-white/70 hover:text-white inline-flex items-center gap-1.5" data-testid="tl-back">
        <ArrowLeft size={12}/> back to academy
      </Link>
      <motion.div initial={{ opacity: 0, y: 18, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                  className="w-full max-w-md rounded-3xl p-8 md:p-10 backdrop-blur-2xl shadow-2xl"
                  style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
               style={{ background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.3)" }}>
            <Certificate size={22} weight="fill" color={GOLD}/>
          </div>
          <h1 className="font-heading text-3xl text-white font-medium">Rescigo Academy</h1>
          <p className="text-sm text-white/70 mt-1">User Login Portal</p>
        </div>

        {step === "email" ? (
          <form onSubmit={submitEmail} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-white/60 block mb-2">I am signing in as</label>
              <div className="grid grid-cols-3 gap-2">
                {[["customer","Customer"],["staff","Staff"],["govt","Government"]].map(([v,l]) => (
                  <button type="button" key={v} onClick={() => setUserType(v)} data-testid={`tl-role-${v}`}
                          className={`h-11 rounded-lg text-xs font-medium border transition ${userType === v ? "text-[#0B2B66] border-transparent" : "text-white/70 border-white/15 hover:border-white/30"}`}
                          style={{ background: userType === v ? GOLD : "transparent" }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-white/60 block mb-2">Verified email</label>
              <div className="relative">
                <EnvelopeSimple size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40"/>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus
                       data-testid="tl-email" placeholder="you@example.com"
                       className="w-full h-12 pl-10 pr-4 rounded-xl text-white placeholder:text-white/30 outline-none transition"
                       style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)" }}/>
              </div>
            </div>
            {err && <div className="text-xs px-3 py-2 rounded-lg border border-red-400/30 bg-red-500/15 text-red-200" data-testid="tl-err">{err}</div>}
            <button type="submit" disabled={busy} data-testid="tl-submit"
                    className="w-full h-12 rounded-xl text-sm font-medium inline-flex items-center justify-center gap-2 shadow-lg transition"
                    style={{ background: GOLD, color: BLUE }}>
              {busy ? "Sending code…" : "Email me the 8-digit code"} <ArrowRight size={14} weight="bold"/>
            </button>
            <div className="text-[11px] text-center text-white/50 pt-2">
              <LockKey size={10} className="inline mr-1"/>
              Access is restricted to verified users only.
            </div>
          </form>
        ) : (
          <form onSubmit={submitOtp} className="space-y-4">
            <div className="text-center text-sm text-white/80">
              A code was sent to <b className="text-white">{email}</b>
            </div>
            <input autoFocus type="text" inputMode="numeric" maxLength={8}
                   value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                   placeholder="••••••••" data-testid="tl-otp"
                   className="w-full h-14 rounded-xl text-center text-2xl font-mono tracking-[0.4em] text-white placeholder:text-white/20 outline-none"
                   style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)" }}/>
            <div className="flex items-center justify-between text-xs text-white/60">
              <span>{ttl > 0 ? <>Expires in <b className="text-white">{mm}:{ss}</b></> : <span className="text-red-300">Code expired</span>}</span>
              <button type="button" onClick={() => setStep("email")} className="underline" data-testid="tl-otp-back">Use different email</button>
            </div>
            {err && <div className="text-xs px-3 py-2 rounded-lg border border-red-400/30 bg-red-500/15 text-red-200" data-testid="tl-otp-err">{err}</div>}
            <button type="submit" disabled={busy || otp.length !== 8} data-testid="tl-otp-submit"
                    className="w-full h-12 rounded-xl text-sm font-medium inline-flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"
                    style={{ background: GOLD, color: BLUE }}>
              {busy ? "Verifying…" : "Sign in"} <ArrowRight size={14} weight="bold"/>
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}

// ============================== HOME ==============================
export function TrainingHome() {
  const me = trainee.get();
  const [data, setData] = useState({ enrollments: [], catalog: [] });
  const [loading, setLoading] = useState(true);
  const [certs, setCerts] = useState([]);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        tapi.get("/training/me/courses"),
        tapi.get("/training/me/certificates"),
      ]);
      setData(r1.data); setCerts(r2.data);
    } catch (e) {
      if (e.response?.status === 401) { trainee.clear(); nav("/training/login"); return; }
      toast.error(fmtErr(e));
    }
    setLoading(false);
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  const enroll = async (course) => {
    try {
      const r = await tapi.post(`/training/me/enroll/${course.id}`);
      if (r.data.status === "awaiting_payment") {
        await launchRazorpayForEnrollment(r.data, course, load);
        return;
      }
      toast.success(`Enrolled in ${course.name}`);
      nav(`/training/course/${course.id}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const logout = () => { trainee.clear(); nav("/training/login"); };

  if (loading) return <FullScreenLoader/>;

  return (
    <div className="min-h-screen bg-[#FAFBFF]" data-testid="training-home">
      <TopBar me={me} onLogout={logout}/>
      <div className="max-w-6xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>Welcome back</div>
          <h1 className="font-heading text-3xl md:text-4xl font-medium mt-1" style={{ color: BLUE }}>{me?.name || me?.email}</h1>
          <p className="text-sm text-[#5C5A55] mt-1">Continue your learning and earn your next certification.</p>
        </motion.div>

        {/* My enrollments */}
        {data.enrollments.length > 0 && (
          <section className="mt-10">
            <SectionTitle>My courses</SectionTitle>
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              {data.enrollments.map((e) => (
                <Link key={e.id} to={`/training/course/${e.course_id}`} data-testid={`my-course-${e.course_id}`}
                      className="bg-white rounded-xl border border-[#0B2B66]/10 p-5 hover:border-[#0B2B66]/30 transition block">
                  <div className="flex items-start justify-between">
                    <GraduationCap size={20} color={BLUE} weight="fill"/>
                    <StatusPill status={e.status}/>
                  </div>
                  <div className="font-heading text-lg mt-3 leading-tight line-clamp-2">{e.course_name}</div>
                  <ProgressBar pct={e.progress_pct || 0}/>
                  <div className="text-xs text-[#5C5A55] mt-2">{e.progress_pct || 0}% complete</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Certificates earned */}
        {certs.length > 0 && (
          <section className="mt-10">
            <SectionTitle>My certificates</SectionTitle>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              {certs.map((c) => (
                <div key={c.id} className="bg-white rounded-xl border border-[#0B2B66]/10 p-5 flex items-center gap-4"
                     data-testid={`my-cert-${c.certificate_no}`}>
                  <div className="w-14 h-14 rounded-lg grid place-items-center text-white flex-shrink-0"
                       style={{ background: `linear-gradient(135deg, ${BLUE}, ${GOLD})` }}>
                    <Certificate size={24} weight="fill"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg leading-tight truncate">{c.course_name || c.cert_type}</div>
                    <div className="text-[10px] font-mono text-[#5C5A55] mt-0.5">{c.certificate_no}</div>
                    <div className="text-xs text-[#5C5A55] mt-0.5">Valid until {c.valid_until}</div>
                  </div>
                  <a href={`${API}/training/certificate/${c.certificate_no}.pdf`} target="_blank" rel="noreferrer"
                     data-testid={`cert-pdf-${c.certificate_no}`}
                     className="h-10 w-10 rounded-md grid place-items-center hover:bg-[#0B2B66]/5" title="Download PDF">
                    <Download size={16} color={BLUE}/>
                  </a>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Catalog */}
        <section className="mt-10">
          <SectionTitle>Available programs</SectionTitle>
          {data.catalog.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-[#0B2B66]/20 p-10 text-center mt-4">
              <Sparkle size={28} color={BLUE} weight="fill" className="mx-auto mb-3"/>
              <div className="text-sm text-[#5C5A55]">New programs are being prepared for your role. Check back soon.</div>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              {data.catalog.map((c) => (
                <div key={c.id} className="bg-white rounded-xl border border-[#0B2B66]/10 p-5 flex flex-col"
                     data-testid={`catalog-${c.id}`}>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>{c.category_name}</div>
                  <div className="font-heading text-lg mt-1 leading-tight">{c.name}</div>
                  <div className="text-sm text-[#5C5A55] mt-2 flex-1">{c.description}</div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm">
                      {c.is_paid ? <b style={{ color: BLUE }}>₹{(c.price_paise/100).toFixed(0)}</b> : <span className="text-[#2D5A27] font-semibold">Free</span>}
                    </div>
                    {c.enrolled ? (
                      <Link to={`/training/course/${c.id}`} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: BLUE }}>
                        Continue <ArrowRight size={11} weight="bold"/>
                      </Link>
                    ) : (
                      <button onClick={() => enroll(c)} data-testid={`enroll-${c.id}`}
                              className="h-9 px-4 rounded-md text-white text-xs font-medium" style={{ background: BLUE }}>
                        Enroll
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// ============================== COURSE PLAYER ==============================
export function TrainingCoursePlayer() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const watchedRef = useRef({});

  const load = useCallback(async () => {
    try {
      const r = await tapi.get(`/training/me/course/${courseId}`);
      setData(r.data);
      // auto-pick the first not-locked, not-complete video
      const firstAvailable = r.data.modules.flatMap(m => m.videos).find(v => !v.locked && !v.progress.completed);
      setActiveVideo(firstAvailable || r.data.modules[0]?.videos?.[0] || null);
    } catch (e) {
      if (e.response?.status === 401) { trainee.clear(); nav("/training/login"); return; }
      toast.error(fmtErr(e));
    }
  }, [courseId, nav]);
  useEffect(() => { load(); }, [load]);

  const markComplete = async (video) => {
    try {
      await tapi.post(`/training/me/video/${video.id}/progress`, {
        watched_seconds: video.duration_seconds || 300, completed: true,
      });
      toast.success("Module completed ✓");
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!data) return <FullScreenLoader/>;

  return (
    <div className="min-h-screen bg-[#FAFBFF]" data-testid="training-course-player">
      <TopBar me={trainee.get()} onLogout={() => { trainee.clear(); nav("/training/login"); }}/>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link to="/training/home" className="text-xs text-[#5C5A55] inline-flex items-center gap-1 hover:text-[#0B2B66]" data-testid="player-back">
          <ArrowLeft size={11}/> back
        </Link>
        <h1 className="font-heading text-3xl mt-3" style={{ color: BLUE }}>{data.course.name}</h1>
        <ProgressBar pct={data.progress_pct}/>
        <div className="text-xs text-[#5C5A55] mt-1">{data.progress_pct}% complete</div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-4">
            {activeVideo ? (
              <div className="bg-black rounded-xl overflow-hidden aspect-video" data-testid="player-video">
                <iframe
                  width="100%" height="100%" className="w-full h-full"
                  src={`https://www.youtube.com/embed/${activeVideo.youtube_id}?rel=0&modestbranding=1&disablekb=1&controls=1&showinfo=0&fs=1&iv_load_policy=3`}
                  title={activeVideo.title} frameBorder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope"
                  allowFullScreen/>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-[#0B2B66]/10 p-10 text-center">
                <PlayCircle size={40} color={BLUE} className="mx-auto mb-2 opacity-40"/>
                <div className="text-sm text-[#5C5A55]">Select a module to start</div>
              </div>
            )}
            {activeVideo && (
              <div className="bg-white rounded-xl border border-[#0B2B66]/10 p-5">
                <h3 className="font-heading text-xl" style={{ color: BLUE }}>{activeVideo.title}</h3>
                {activeVideo.progress?.completed ? (
                  <div className="inline-flex items-center gap-2 mt-3 text-sm text-[#2D5A27]">
                    <CheckCircle size={16} weight="fill"/> Completed
                  </div>
                ) : (
                  <button onClick={() => markComplete(activeVideo)} data-testid="player-complete"
                          className="mt-3 h-10 px-4 rounded-md text-white text-sm font-medium inline-flex items-center gap-2" style={{ background: BLUE }}>
                    <CheckCircle size={14} weight="fill"/> Mark as completed
                  </button>
                )}
                <div className="text-[11px] text-[#8A8780] mt-3">
                  Please watch the entire video before marking complete. Do not skip or fast-forward —
                  the video must be watched end-to-end for compliance.
                </div>
              </div>
            )}
            {data.exam_ready && (
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }}
                          className="rounded-xl p-6 text-white" style={{ background: `linear-gradient(135deg, ${BLUE}, ${GOLD})` }}>
                <div className="flex items-center gap-3">
                  <ShieldStar size={28} weight="fill"/>
                  <div className="flex-1">
                    <div className="font-heading text-xl">You're ready for the exam!</div>
                    <div className="text-sm text-white/85">All modules complete. Pass the exam to earn your certificate.</div>
                  </div>
                  <Link to={`/training/exam/${courseId}`} data-testid="player-go-exam"
                        className="h-11 px-5 rounded-md font-medium inline-flex items-center gap-2 whitespace-nowrap"
                        style={{ background: "white", color: BLUE }}>
                    Take the exam <ArrowRight size={14} weight="bold"/>
                  </Link>
                </div>
              </motion.div>
            )}
          </div>

          <div className="space-y-3">
            {data.modules.map((m) => (
              <div key={m.id} className="bg-white rounded-xl border border-[#0B2B66]/10 p-4">
                <div className="flex items-center gap-2">
                  {m.completed ? <CheckCircle size={14} weight="fill" color="#2D5A27"/>
                    : m.locked ? <LockKey size={12} color="#8A8780"/> : <PlayCircle size={14} color={BLUE}/>}
                  <div className="font-medium text-sm flex-1">{m.name}</div>
                </div>
                <div className="mt-2 space-y-1">
                  {m.videos.map((v) => (
                    <button key={v.id} disabled={v.locked} onClick={() => setActiveVideo(v)} data-testid={`v-${v.id}`}
                            className={`w-full text-left flex items-center gap-2 text-xs px-2 py-1.5 rounded transition ${activeVideo?.id === v.id ? "bg-[#0B2B66]/10 text-[#0B2B66]" : "hover:bg-[#0B2B66]/5"} ${v.locked ? "opacity-40 cursor-not-allowed" : ""}`}>
                      {v.progress.completed ? <CheckCircle size={10} weight="fill" color="#2D5A27"/>
                        : v.locked ? <LockKey size={9}/> : <PlayCircle size={10}/>}
                      <span className="flex-1 truncate">{v.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================== EXAM ==============================
export function TrainingExam() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadExam = useCallback(async () => {
    try {
      const r = await tapi.get(`/training/me/exam/${courseId}`);
      setExam(r.data); setAnswers({}); setResult(null);
    } catch (e) {
      toast.error(fmtErr(e));
      if (e.response?.status === 400) nav(`/training/course/${courseId}`);
    }
  }, [courseId, nav]);
  useEffect(() => { loadExam(); }, [loadExam]);

  const submit = async () => {
    if (Object.keys(answers).length < (exam?.questions.length || 0)) {
      return toast.error("Please answer all questions");
    }
    setBusy(true);
    try {
      const r = await tapi.post(`/training/me/exam/${courseId}/submit`, { answers });
      setResult(r.data);
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  if (!exam) return <FullScreenLoader/>;

  if (result) {
    return (
      <div className="min-h-screen bg-[#FAFBFF] flex items-center justify-center p-6" data-testid="training-exam-result">
        <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}
                    className="bg-white rounded-2xl p-10 max-w-md text-center border border-[#0B2B66]/15 shadow-xl">
          {result.passed ? (
            <>
              <div className="w-20 h-20 rounded-full mx-auto grid place-items-center text-white mb-5"
                   style={{ background: `linear-gradient(135deg, ${BLUE}, ${GOLD})` }}>
                <Certificate size={36} weight="fill"/>
              </div>
              <h1 className="font-heading text-3xl" style={{ color: BLUE }}>Congratulations! 🎉</h1>
              <p className="text-sm text-[#5C5A55] mt-2">You scored <b className="text-[#1A1A1A]">{result.score_pct}%</b> — well above the {result.threshold}% threshold.</p>
              {result.certificate && (
                <>
                  <div className="mt-5 p-4 rounded-lg border border-[#0B2B66]/20 bg-[#FAFBFF]">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">Certificate number</div>
                    <div className="font-mono text-sm mt-1">{result.certificate.certificate_no}</div>
                  </div>
                  <a href={`${API}/training/certificate/${result.certificate.certificate_no}.pdf`} target="_blank" rel="noreferrer"
                     data-testid="exam-download-cert"
                     className="mt-5 w-full h-12 rounded-md inline-flex items-center justify-center gap-2 text-white font-medium" style={{ background: BLUE }}>
                    <Download size={14} weight="bold"/> Download Certificate PDF
                  </a>
                </>
              )}
              <Link to="/training/home" className="block mt-3 text-xs text-[#5C5A55] hover:text-[#0B2B66]" data-testid="exam-home">
                Back to home
              </Link>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full mx-auto grid place-items-center text-white mb-5" style={{ background: "#C25D38" }}>
                <ArrowsClockwise size={36} weight="bold"/>
              </div>
              <h1 className="font-heading text-3xl text-[#1A1A1A]">Not quite there yet</h1>
              <p className="text-sm text-[#5C5A55] mt-2">You scored <b>{result.score_pct}%</b> — you need {result.threshold}% to pass. Don't give up!</p>
              <button onClick={loadExam} data-testid="exam-retake"
                      className="mt-5 w-full h-12 rounded-md text-white font-medium inline-flex items-center justify-center gap-2" style={{ background: BLUE }}>
                <ArrowsClockwise size={14} weight="bold"/> Try again
              </button>
              <Link to={`/training/course/${courseId}`} className="block mt-3 text-xs text-[#5C5A55] hover:text-[#0B2B66]">
                Review the modules
              </Link>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFF]" data-testid="training-exam">
      <TopBar me={trainee.get()} onLogout={() => { trainee.clear(); nav("/training/login"); }}/>
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
          <ShieldStar size={11} weight="fill"/> Final Examination · {exam.questions.length} questions · Pass threshold {exam.pass_threshold}%
        </div>
        <h1 className="font-heading text-3xl mt-2" style={{ color: BLUE }}>Take your time — you've got this.</h1>

        <div className="mt-8 space-y-6">
          {exam.questions.map((q, i) => (
            <div key={q.qid} className="bg-white rounded-xl border border-[#0B2B66]/10 p-5" data-testid={`q-${q.qid}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-1">Question {i + 1}</div>
              <div className="font-heading text-lg">{q.question}</div>
              <div className="mt-4 space-y-2">
                {q.options.map((opt, oi) => (
                  <label key={`${q.qid}-opt-${oi}`} data-testid={`q-${q.qid}-opt-${oi}`}
                         className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${answers[q.qid] === oi ? "border-[#0B2B66] bg-[#0B2B66]/5" : "border-[#0B2B66]/10 hover:border-[#0B2B66]/30"}`}>
                    <input type="radio" name={q.qid} checked={answers[q.qid] === oi}
                           onChange={() => setAnswers({ ...answers, [q.qid]: oi })}
                           className="mt-1 accent-[#0B2B66]"/>
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <button onClick={submit} disabled={busy} data-testid="exam-submit"
                className="mt-6 w-full h-14 rounded-md text-white font-medium inline-flex items-center justify-center gap-2 text-base" style={{ background: BLUE }}>
          {busy ? "Submitting…" : "Submit exam"} <ArrowRight size={14} weight="bold"/>
        </button>
      </div>
    </div>
  );
}

// ============================== VERIFY PUBLIC ==============================
export function TrainingVerify() {
  const { certNo } = useParams();
  const [result, setResult] = useState(null);
  const [query, setQuery] = useState(certNo || "");

  const check = useCallback(async (no) => {
    try {
      const r = await axios.get(`${API}/training/public/verify/${no}`);
      setResult(r.data);
    } catch { setResult({ valid: false, reason: "Verification failed" }); }
  }, []);
  useEffect(() => { if (certNo) check(certNo); }, [certNo, check]);

  return (
    <div className="min-h-screen bg-[#FAFBFF] p-6" data-testid="training-verify">
      <div className="max-w-2xl mx-auto pt-12">
        <Link to="/training" className="text-xs text-[#5C5A55] inline-flex items-center gap-1 hover:text-[#0B2B66]">
          <ArrowLeft size={11}/> back to academy
        </Link>
        <div className="text-center mt-6 mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl text-white mb-3" style={{ background: `linear-gradient(135deg, ${BLUE}, ${GOLD})` }}>
            <QrCode size={24} weight="fill"/>
          </div>
          <h1 className="font-heading text-4xl" style={{ color: BLUE }}>Verify Certificate</h1>
          <p className="text-sm text-[#5C5A55] mt-1">Enter a certificate number to check its authenticity.</p>
        </div>
        <div className="bg-white rounded-xl border border-[#0B2B66]/15 p-6">
          <div className="flex gap-2">
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="verify-input"
                   placeholder="RSG-CERT-YYYY-NNNNNN"
                   className="flex-1 h-11 px-3 border border-[#0B2B66]/20 rounded-md text-sm font-mono outline-none focus:border-[#0B2B66]"/>
            <button onClick={() => check(query.trim())} data-testid="verify-submit"
                    className="h-11 px-5 rounded-md text-white font-medium text-sm" style={{ background: BLUE }}>
              Verify
            </button>
          </div>
          {result && (
            <div className="mt-5 p-5 rounded-lg border" data-testid="verify-result"
                 style={{ borderColor: result.valid ? "#2D5A27" : "#C25D38",
                          background: result.valid ? "#E8EFE6" : "#FBE7DD" }}>
              {result.valid ? (
                <>
                  <div className="inline-flex items-center gap-2 font-heading text-lg text-[#2D5A27]">
                    <CheckCircle size={18} weight="fill"/> Authentic Certificate
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <KV k="Certificate No" v={result.certificate_no} mono/>
                    <KV k="Type" v={result.cert_type}/>
                    <KV k="Recipient" v={result.recipient_name}/>
                    <KV k="Course" v={result.course_name || "—"}/>
                    <KV k="Issued" v={(result.issued_at || "").slice(0,10)}/>
                    <KV k="Valid until" v={result.valid_until}/>
                  </div>
                  {result.expired && (
                    <div className="mt-3 text-xs px-3 py-2 rounded bg-[#F9E4B7] text-[#7A5A11]">⚠️ This certificate has expired.</div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2 text-[#7A2E18]">
                  <LockKey size={16}/> <b>{result.reason || "Invalid certificate"}</b>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================== Bits ==============================
const TopBar = ({ me, onLogout }) => (
  <div className="bg-white border-b border-[#0B2B66]/10 sticky top-0 z-30 backdrop-blur-xl">
    <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
      <Link to="/training/home" className="flex items-center gap-2 font-heading">
        <Certificate size={18} weight="fill" color={GOLD}/> <span style={{ color: BLUE }}>Rescigo Academy</span>
      </Link>
      <div className="flex items-center gap-3 text-xs">
        <div className="hidden sm:block text-[#5C5A55]">{me?.email} · <span className="uppercase tracking-wider" style={{ color: GOLD }}>{me?.user_type}</span></div>
        <button onClick={onLogout} data-testid="top-logout" className="h-8 px-3 rounded-md border border-[#0B2B66]/20 hover:bg-[#0B2B66]/5 inline-flex items-center gap-1 text-xs">
          <SignOut size={11}/> Sign out
        </button>
      </div>
    </div>
  </div>
);

const FullScreenLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FAFBFF]">
    <Clock size={24} className="animate-pulse" color={BLUE}/>
  </div>
);
const ProgressBar = ({ pct }) => (
  <div className="mt-3 h-1.5 rounded-full overflow-hidden bg-[#0B2B66]/10">
    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${BLUE}, ${GOLD})` }}/>
  </div>
);
const StatusPill = ({ status }) => {
  const map = { in_progress: ["In progress", "#F9E4B7", "#7A5A11"],
                exam_ready: ["Exam ready", "#D4E0D1", "#1A3317"],
                passed: ["Passed ✓", "#D4E0D1", "#1A3317"],
                failed_attempt: ["Retry", "#F4C7B9", "#7A2E18"],
                awaiting_payment: ["Awaiting payment", "#F9E4B7", "#7A5A11"] };
  const [label, bg, color] = map[status] || [status, "#E5E2D9", "#5C5A55"];
  return <span className="px-2 py-1 rounded text-[9px] uppercase tracking-[0.16em] font-semibold" style={{ background: bg, color }}>{label}</span>;
};
const SectionTitle = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5C5A55]">{children}</div>
);
const KV = ({ k, v, mono }) => (
  <div><div className="text-[9px] uppercase tracking-[0.18em] text-[#5C5A55]">{k}</div>
  <div className={mono ? "font-mono text-xs mt-0.5" : "text-sm mt-0.5"}>{v}</div></div>
);

// Wrap auth-required pages
export const RequiredHome = () => <RequireTrainee><TrainingHome/></RequireTrainee>;
export const RequiredCourse = () => <RequireTrainee><TrainingCoursePlayer/></RequireTrainee>;
export const RequiredExam = () => <RequireTrainee><TrainingExam/></RequireTrainee>;
